import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useCallback, useMemo } from 'react';
import { ActorRefFrom } from 'xstate';
import { EmptyState, FormDialog, TableCard } from '~/components';
import { BasicGrid } from '~/components/agGrid/Grid';
import { OptionWithIdType } from '~/features/types';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { useLoadedProvider } from '../../account/vaultSelectors';
import { academicsMachine } from '../academics.machine';
import { SpecCertOptions, TrainingFormValues } from '../types';
import { createTrainingCols } from './constants';
import { EditTrainingForm } from './EditTrainingForm';
import { TrainingWizard } from './trainingWizard/TrainingWizard';

interface Props {
  actor: ActorRefFrom<typeof academicsMachine>;
}
const TAGS = CT.academics.training;

const TrainingNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.academics}
    title="No training records yet"
    cyTag={TAGS.trainingTable.emptyState}
  />
);

export const TrainingDetails = (props: Props) => {
  const { actor } = props;

  // Narrow field-level selectors — only re-render when the specific field changes
  const openEditTrainingModal = useSelector(actor, (state) => state.context.openEditTrainingModal);
  const loading = useSelector(actor, (state) => state.context.loading);
  const trainingWizard = useSelector(actor, (state) => state.context.trainingWizard);
  const providerId = useSelector(actor, (state) => state.context.providerId);
  const trainingForm = useSelector(actor, (state) => state.context.trainingForm);
  const trainings = useSelector(actor, (state) => state.context.trainings);
  const _specCertOptions = useSelector(actor, (state) => state.context._specCertOptions);
  const _specialtyBoardOptions = useSelector(
    actor,
    (state) => state.context._specialtyBoardOptions
  );
  const _specSubCertOptions = useSelector(actor, (state) => state.context._specSubCertOptions);

  const { provider, vaultRef } = useLoadedProvider();
  if (!provider || !vaultRef) {
    return null;
  }

  const setSubSpecCertOptions = useCallback(
    (options: SpecCertOptions[]) => {
      actor.send({ type: 'ON_SET_SUB_SPEC_CERT_OPTIONS', values: options });
    },
    [actor]
  );

  const setSpecCertOptions = useCallback(
    (values: SpecCertOptions[]) => {
      actor.send({ type: 'ON_SET_SPEC_CERT_OPTIONS', values });
    },
    [actor]
  );

  const handleOpen = useCallback(() => {
    actor.send({ type: 'ON_OPEN_WIZARD' });
  }, [actor]);

  const handleDelete = useCallback(
    (id: number) =>
      actor.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id, type: 'TRAINING' } }),
    [actor]
  );

  const handleEdit = useCallback(
    (id: number) => actor.send({ type: 'ON_EDIT', payload: { id } }),
    [actor]
  );

  const editTraining = useCallback(
    (updatedTrainingValues: TrainingFormValues) => {
      actor.send({ type: 'ON_UPDATE', values: updatedTrainingValues });
    },
    [actor]
  );

  const handleClose = useCallback(() => actor.send({ type: 'ON_CLOSE_MODAL' }), [actor]);

  const wizardNext = useCallback(
    (values: Partial<TrainingFormValues>) => {
      actor.send({ type: 'ON_WIZ_NEXT_PAGE', values });
    },
    [actor]
  );

  const wizardPrev = useCallback(
    (values?: Partial<TrainingFormValues>) => {
      actor.send({ type: 'ON_WIZ_PREV_PAGE', values });
    },
    [actor]
  );

  const wizardSubmit = useCallback(
    (values: Omit<TrainingFormValues, 'id'>) => {
      actor.send({ type: 'ON_SUBMIT', values });
    },
    [actor]
  );

  const setSpecBoardOptions = useCallback(
    (options: OptionWithIdType[]) => {
      actor.send({ type: 'ON_SET_SPEC_BOARD_OPTIONS', values: options });
    },
    [actor]
  );

  const colDefs = useMemo(
    () => createTrainingCols({ handleDelete, handleEdit }),
    [handleDelete, handleEdit]
  );

  return (
    <>
      <TableCard
        mobilePageHeader
        title="Training"
        addLabel="Add Training"
        onAddClick={handleOpen}
        addBtnTag={TAGS.addBtn}
        layoutTag={TAGS.layout}
      >
        <BasicGrid
          newRecordsTable="training"
          data={trainings}
          loading={loading}
          colDefs={colDefs}
          pagination={true}
          cyTag={TAGS.grid}
          noRowsOverlayComponent={TrainingNoRowsOverlay}
        />
      </TableCard>
      <FormDialog
        title={'Add New Training '}
        editing={false}
        creating={trainingWizard.open}
        onClose={handleClose}
      >
        <TrainingWizard
          _specCertOptions={_specCertOptions}
          _specialtyBoardOptions={_specialtyBoardOptions}
          _specSubCertOptions={_specSubCertOptions}
          setSpecCertOptions={setSpecCertOptions}
          setSubSpecCertOptions={setSubSpecCertOptions}
          form={trainingForm}
          closeWizard={handleClose}
          wizardNext={wizardNext}
          wizardPrev={wizardPrev}
          wizardSubmit={wizardSubmit}
          currentPage={trainingWizard.currentPage}
          providerCategoryId={provider?.type?.providerCategoryId ?? 0}
          providerId={providerId}
          setSpecBoardOptions={setSpecBoardOptions}
        />
      </FormDialog>
      <FormDialog
        title={'Training'}
        editing={openEditTrainingModal}
        creating={false}
        onClose={handleClose}
      >
        <EditTrainingForm
          trainingForm={trainingForm}
          editTraining={editTraining}
          handleClose={handleClose}
          providerCategoryId={provider?.type?.providerCategoryId ?? 0}
          providerId={providerId}
        />
      </FormDialog>
    </>
  );
};
